/* eslint-disable import/prefer-default-export,import/no-unresolved,react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MAIN_ICON_COLORS } from 'assets/components';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { OnboardingContext } from '../../../../contexts/onboarding/context';
import { OnboardingRegistrationSteps } from './OnboardingRegistrationSteps';
import ModalRoot, { Modal, ModalBody, ModalHeader } from '../../../../components/Modal';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { onboardingScreens } from '../constants';
import Icon from '../../../../components/Icon';
import { ICONS } from '../../../../components/Icon/constants';
import { getCurrentLanguage, switchLanguage } from '../../../../i18n';

export const OnboardingLayout = ({ children, className, currentScreen }) => {
  const { state, openScreen, closeRegistrationStepsPopup, updateMainSettings } =
    useContext(OnboardingContext);

  const { t: translate } = useTranslation();

  const [settings] = useSetting(
    'app_features',
    [
      'feature_allow_separate_login_flow',
      'feature_allow_totp',
      'feature_enable_partial_registration',
      'feature_toggle_enable_postcode_verification_signup',
    ],
    false,
    false
  );

  const renderedComponent = useMemo(() => {
    if (Array.isArray(children)) {
      openScreen(currentScreen);
      return children?.filter((child) => child.props.keyName === currentScreen);
    }
    openScreen(currentScreen);
    return [children]?.filter((child) => child.props.keyName === currentScreen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, currentScreen]);

  const isLoginFlow = useMemo(
    () => [onboardingScreens.LOGIN, onboardingScreens.TFA].includes(currentScreen),
    [currentScreen]
  );

  const isResetPasswordFlow = useMemo(
    () =>
      [
        onboardingScreens.RESET_PASSWORD_REQUEST,
        onboardingScreens.CONFIRM_RESET_PASSWORD_EMAIL,
        onboardingScreens.CREATE_NEW_PASSWORD,
        onboardingScreens.TFA,
      ].includes(currentScreen),
    [currentScreen]
  );

  useEffect(() => {
    if (settings && !state?.mainSettings) {
      updateMainSettings(settings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, state?.mainSettings]);

  const tooltipRef = useRef(null);
  const langBtnRef = useRef(null);
  const browserLangBtnRef = useRef(null);

  const [showLanguageSelector, setShowLanguageSelector] = React.useState(false);

  const [selectedLang, setSelectedLang] = useState('en');

  useEffect(() => {
    const currentLang = getCurrentLanguage();
    setSelectedLang(currentLang);
  }, []);

  const availableLangs = useMemo(
    () => [
      {
        value: 'en',
        label: 'lang_name_en',
      },
      {
        value: 'de',
        label: 'lang_name_de',
      },
    ],
    []
  );

  const handleClickOutside = (e) => {
    if (
      tooltipRef.current &&
      !tooltipRef.current.contains(e.target) &&
      browserLangBtnRef.current &&
      !browserLangBtnRef.current.contains(e.target)
    ) {
      setShowLanguageSelector(false);
      document.removeEventListener('mousemove', handleClickOutside);
    }
  };

  useEffect(() => () => document.removeEventListener('mousemove', handleClickOutside), []);

  const handleOpenLanguageSelector = () => {
    setShowLanguageSelector(true);
    document.addEventListener('mousemove', handleClickOutside);
  };

  const handleClickOutsideMobile = (e) => {
    if (
      (!tooltipRef.current || (tooltipRef.current && !tooltipRef.current.contains(e.target))) &&
      langBtnRef.current &&
      !langBtnRef.current.contains(e.target)
    ) {
      setShowLanguageSelector(false);
      document.removeEventListener('click', handleClickOutsideMobile);
    }
  };

  useEffect(() => () => document.removeEventListener('click', handleClickOutsideMobile), []);

  const handleOpenLanguageSelectorMobile = () => {
    setShowLanguageSelector((prev) => !prev);
    document.addEventListener('click', handleClickOutsideMobile);
  };

  return (
    <div className="d-flex flex-row h-100vh main-layout">
      <div className="d-flex flex-column flex-fill align-items-center h-100vh position-relative">
        {isLoginFlow && (
          <>
            <div
              ref={browserLangBtnRef}
              className="position-fixed d-none d-md-flex justify-content-end"
              style={{ width: '14rem', height: '9rem', top: '2rem', right: '2rem', zIndex: 40 }}>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <button
                data-tooltip-id="language-selector"
                type="button"
                className=" btn-primary-outline px-2"
                style={{ width: '40px', height: '40px', outline: 'none' }}
                onMouseEnter={() => {
                  handleOpenLanguageSelector();
                }}>
                <Icon icon={ICONS.GLOBE_LANG} color={MAIN_ICON_COLORS.WHITE} />
              </button>
            </div>
            {/* mobile */}
            <div
              className="position-fixed d-block d-md-none"
              style={{ top: '2rem', right: '2rem', zIndex: 40 }}>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <button
                ref={langBtnRef}
                data-tooltip-id="language-selector"
                type="button"
                className=" btn-primary-outline px-2"
                style={{ width: '40px', height: '40px', outline: 'none' }}
                onClick={() => {
                  handleOpenLanguageSelectorMobile();
                }}>
                <Icon icon={ICONS.GLOBE_LANG} color={MAIN_ICON_COLORS.WHITE} />
              </button>
            </div>
          </>
        )}
        <div
          className={`d-flex flex-column w-100 position-relative h-100 px-3 px-md-0 cryptodraft-main-layout ${className}`}
          style={{
            maxWidth: '33.5rem',
            transitionProperty: 'margin',
            transitionDuration: '300ms',
          }}>
          {renderedComponent}
        </div>
      </div>
      {!isLoginFlow && !isResetPasswordFlow && (
        <div className="d-none d-md-block h-100 onboarding-sidebar bg-lighter px-5">
          <OnboardingRegistrationSteps />
        </div>
      )}
      {state?.registrationStepsOpened && (
        <ModalRoot>
          <Modal
            contentStyle={{ maxHeight: '41rem' }}
            specialMobileContentClass="w-auto rounded-0 rounded-top py-3 px-0">
            <ModalHeader>
              <i
                onClick={closeRegistrationStepsPopup}
                onKeyDown={closeRegistrationStepsPopup}
                role="button"
                className="close-btn cursor-pointer icon icon-close-other text-dark"
                tabIndex={-1}
                aria-label="Close"
              />
            </ModalHeader>
            <ModalBody>
              <OnboardingRegistrationSteps />
            </ModalBody>
          </Modal>
        </ModalRoot>
      )}
      <Tooltip
        id="language-selector"
        isOpen={showLanguageSelector}
        place="bottom"
        variant="dark"
        opacity={1}
        style={{ zIndex: 40, padding: '0px', pointerEvents: 'auto' }}
        className="rounded border-1">
        <ul ref={tooltipRef} className="d-flex flex-column list-unstyled m-0">
          {availableLangs.map((lang) => (
            <li
              role="presentation"
              tabIndex={-1}
              onKeyDown={null}
              onClick={() => {
                setSelectedLang(lang.value);
                switchLanguage(lang.value);
                setShowLanguageSelector(false);
              }}
              className={`cursor-pointer px-5 py-2 ${
                selectedLang === lang.value ? 'text-primary' : 'text-hover'
              }`}
              key={lang.value}>
              {translate(lang.label)}
            </li>
          ))}
        </ul>
      </Tooltip>
    </div>
  );
};

OnboardingLayout.propTypes = {
  currentScreen: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  className: PropTypes.string,
};

OnboardingLayout.defaultProps = {
  children: null,
  className: '',
};
